import Logo from "./Logo";
import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Menu,
  X,
  ChevronDown,
  Ship,
  Package,
  Warehouse,
  Truck,
  Globe2,
  Database,
  MapPin,
  CircleDot,
  User,
} from "lucide-react";

export default function Navbar({ currentTab, onTabChange }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownMotion = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.18, ease: "easeOut" },
  };

  const services = [
    {
      id: "service_express_parcel",
      name: "Express Parcel",
      desc: "Door-to-door, air-freight, and surface delivery networks",
      icon: Package,
    },
    {
      id: "service_warehousing",
      name: "Warehousing",
      desc: "Multi-tenant fulfillment facilities and custom inventory tracking",
      icon: Warehouse,
    },
    {
      id: "service_part_truckload",
      name: "Part Truckload",
      desc: "LTL shipping with extensive pan-India consolidation",
      icon: Truck,
    },
    {
      id: "service_full_truckload",
      name: "Full Truckload",
      desc: "Point-to-point dedicated containers with safety guarantees",
      icon: Truck,
    },
    {
      id: "service_international",
      name: "International",
      desc: "Global freight forwarding, cross-border custom clearances",
      icon: Globe2,
    },
    { id: "service_local_delivery", name: "Local Delivery", desc: "City pickups and local delivery planning", icon: Truck },
    {
      id: "service_transport_one",
      name: "TransportOne",
      desc: "Connected freight planning and operations workflows",
      icon: Ship,
    },
    {
      id: "service_data_intelligence",
      name: "Data Intelligence",
      desc: "Dynamic route planners, automated cargo dimensions",
      icon: Database,
    },
    {
      id: "service_jpsy_maps",
      name: "JPSY Maps",
      desc: "Pin-code geofencing, route visualization APIs",
      isBeta: true,
      icon: MapPin,
    },
  ];

  const solutions = [
    {
      id: "solution_d2c_brands",
      name: "D2C Brands",
      desc: "Fulfillment and Supply Chain for modern fast-growing D2C retail",
      icon: Package,
    },
    {
      id: "solution_personal_courier",
      name: "Personal Courier",
      desc: "Contactless home pickup and automated tracking",
      icon: Ship,
    },
    {
      id: "solution_b2b_enterprises",
      name: "B2B Enterprises",
      desc: "Integrated industrial supply-chains & high volume freight",
      icon: Warehouse,
    },
    {
      id: "solution_rto_predictor",
      name: "RTO Predictor",
      desc: "Pre-scan risk engine to reduce Return-To-Origin failures",
      icon: Database,
    },
  ];

  const partners = [
    {
      id: "partner_franchise",
      name: "Franchise Opportunities",
      desc: "Open a local booking store in your pincode",
      icon: Warehouse,
    },
    {
      id: "partner_delivery_partner",
      name: "Delivery Partner",
      desc: "Attach your local delivery hubs to our network",
      icon: Ship,
    },
    {
      id: "partner_fleet_owners",
      name: "Fleet Owners",
      desc: "Attach your commercial vehicles and lines",
      icon: Truck,
    },
    { id: "partner_logistics_school", name: "Delivery School of Supply Chain", desc: "Learning pathways and institutional collaboration", icon: Warehouse },
  ];

  const company = [
    {
      id: "company_about_us",
      name: "About Us",
      desc: "Our corporate desk, vision, and growth scale",
      icon: CircleDot,
    },
    {
      id: "company_governance",
      name: "Governance",
      desc: "Ethical board guidelines and sustainability plans",
      icon: Database,
    },
    {
      id: "company_press_release",
      name: "Press Release",
      desc: "Publications, partnerships and regional news updates",
      icon: Package,
    },
    {
      id: "company_careers",
      name: "Careers",
      desc: "Build the future of technology Supply Chain with us",
      icon: User,
    },
    { id: "company_blog", name: "Blog", desc: "Supply Chain insights and shipping playbooks", icon: Package },
  ];

  const shipNowMenu = [
    {
      id: "dashboard",
      tab: "booking",
      name: "Business Shipments",
      desc: "Book bulk merchant cargo and track payouts",
    },
    {
      id: "dashboard",
      tab: "booking",
      name: "Local Delivery",
      desc: "Calculate hyper-local pin delivery prices",
    },
    {
      id: "dashboard",
      tab: "booking",
      name: "National Courier",
      desc: "Register parcel home pickup in seconds",
    },
  ];

  const handleItemClick = (tabId) => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
    onTabChange(tabId);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleShipNowClick = (tabId, subTab) => {
    setActiveDropdown(null);
    setMobileMenuOpen(false);
    onTabChange(tabId);
    setTimeout(() => {
      const event = new CustomEvent("jpsy-navigate", {
        detail: { tab: subTab },
      });
      window.dispatchEvent(event);
    }, 150);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-[#263f98] via-[#1f2f82] to-[#21185f] text-white border-b border-white/15 shadow-sm">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-6 h-28">
          {/* JPSY brand */}
          <button
            className="flex h-20 w-[240px] items-center overflow-hidden cursor-pointer"
            onClick={() => handleItemClick("home")}
          >
            <Logo
              className="h-full w-full object-contain object-left"
            />
          </button>

          {/* DESKTOP NAVIGATION */}
          <nav className="hidden lg:flex items-center justify-center gap-6">
            {/* Services Dropdown */}
            <div
              className="relative"
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                onMouseEnter={() => setActiveDropdown("services")}
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "services" ? null : "services",
                  )
                }
                className={`flex items-center gap-1 text-[22px] font-medium py-3 border-b-4 cursor-pointer transition-colors ${
                  activeDropdown === "services"
                    ? "text-white border-white"
                    : "text-slate-100 border-transparent hover:text-white"
                }`}
              >
                Services
              </button>

              <AnimatePresence>
                {activeDropdown === "services" && (
                  <motion.div
                    {...dropdownMotion}
                    className="absolute left-0 top-full w-[260px] bg-white border border-slate-100 rounded-md shadow-2xl py-3 z-50"
                  >
                    {services.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item.id)}
                        className="relative flex w-full items-center gap-3 px-7 py-3 text-left hover:bg-slate-50 transition-colors duration-300 cursor-pointer group"
                      >
                        <item.icon className="h-6 w-6 shrink-0 text-slate-500 group-hover:text-[#263f98]" />
                        <span className="flex min-w-0 flex-1 items-center gap-2 text-[20px] font-normal leading-tight text-slate-900">
                          {item.name}
                          {item.isBeta && (
                            <span className="text-[14px] font-normal text-[#263f98]">
                              Beta
                            </span>
                          )}
                        </span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Solutions Dropdown */}
            <div
              className="relative"
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                onMouseEnter={() => setActiveDropdown("solutions")}
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "solutions" ? null : "solutions",
                  )
                }
                className={`flex items-center gap-1 text-[22px] font-medium py-3 border-b-4 cursor-pointer transition-colors ${
                  activeDropdown === "solutions"
                    ? "text-white border-white"
                    : "text-slate-100 border-transparent hover:text-white"
                }`}
              >
                Solutions
              </button>

              <AnimatePresence>
                {activeDropdown === "solutions" && (
                  <motion.div
                    {...dropdownMotion}
                    className="absolute left-0 top-full w-[260px] bg-white border border-slate-100 rounded-md shadow-2xl py-3 z-50"
                  >
                    {solutions.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item.id)}
                        className="relative flex w-full items-center gap-3 px-7 py-3 text-left hover:bg-slate-50 transition-colors duration-300 cursor-pointer group"
                      >
                        <item.icon className="h-6 w-6 shrink-0 text-slate-500 group-hover:text-[#263f98]" />
                        <span className="text-[20px] font-normal leading-tight text-slate-900">
                          {item.name}
                        </span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Partners Dropdown */}
            <div
              className="relative"
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                onMouseEnter={() => setActiveDropdown("partner")}
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "partner" ? null : "partner",
                  )
                }
                className={`flex items-center gap-1 text-[22px] font-medium py-3 border-b-4 cursor-pointer transition-colors ${
                  activeDropdown === "partner"
                    ? "text-white border-white"
                    : "text-slate-100 border-transparent hover:text-white"
                }`}
              >
                Partner
              </button>

              <AnimatePresence>
                {activeDropdown === "partner" && (
                  <motion.div
                    {...dropdownMotion}
                    className="absolute left-0 top-full w-[260px] bg-white border border-slate-100 rounded-md shadow-2xl py-3 z-50"
                  >
                    {partners.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item.id)}
                        className="relative flex w-full items-center gap-3 px-7 py-3 text-left hover:bg-slate-50 transition-colors duration-300 cursor-pointer group"
                      >
                        <item.icon className="h-6 w-6 shrink-0 text-slate-500 group-hover:text-[#263f98]" />
                        <span className="text-[20px] font-normal leading-tight text-slate-900">
                          {item.name}
                        </span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Company Dropdown */}
            <div
              className="relative"
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                onMouseEnter={() => setActiveDropdown("company")}
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "company" ? null : "company",
                  )
                }
                className={`flex items-center gap-1 text-[22px] font-medium py-3 border-b-4 cursor-pointer transition-colors ${
                  activeDropdown === "company"
                    ? "text-white border-white"
                    : "text-slate-100 border-transparent hover:text-white"
                }`}
              >
                Company
              </button>

              <AnimatePresence>
                {activeDropdown === "company" && (
                  <motion.div
                    {...dropdownMotion}
                    className="absolute left-0 top-full w-[260px] bg-white border border-slate-100 rounded-md shadow-2xl py-3 z-50"
                  >
                    {company.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item.id)}
                        className="relative flex w-full items-center gap-3 px-7 py-3 text-left hover:bg-slate-50 transition-colors duration-300 cursor-pointer group"
                      >
                        <item.icon className="h-6 w-6 shrink-0 text-slate-500 group-hover:text-[#263f98]" />
                        <span className="text-[20px] font-normal leading-tight text-slate-900">
                          {item.name}
                        </span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Investors Direct Link Tab */}
            <button
              onClick={() => handleItemClick("investors")}
              className={`text-[22px] font-medium transition-colors cursor-pointer py-3 ${
                currentTab === "investors"
                  ? "text-white border-b-2 border-white"
                  : "text-slate-100 hover:text-white"
              }`}
            >
              Investors
            </button>

            {/* Track Direct Link Tab */}
            <button
              onClick={() => handleItemClick("tracking")}
              className={`text-[22px] font-medium transition-colors cursor-pointer py-3 ${
                currentTab === "tracking"
                  ? "text-white border-b-2 border-white"
                  : "text-slate-100 hover:text-white"
              }`}
            >
              Track
            </button>

            {/* Support Direct Link Tab */}
            <button
              onClick={() => handleItemClick("support")}
              className={`text-[22px] font-medium transition-colors cursor-pointer py-3 ${
                currentTab === "support"
                  ? "text-white border-b-2 border-white"
                  : "text-slate-100 hover:text-white"
              }`}
            >
              Support
            </button>
          </nav>

          {/* RIGHT ACTION BUTTONS - Styled exact replica of white rect buttons in Screenshot 1 & 6 */}
          <div className="hidden lg:flex items-center justify-end">
            {/* SHIP NOW WHITE dropdown button */}
            <div
              className="relative"
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                onMouseEnter={() => setActiveDropdown("shipNow")}
                onClick={() =>
                  setActiveDropdown(
                    activeDropdown === "shipNow" ? null : "shipNow",
                  )
                }
                className="bg-white hover:bg-slate-50 text-slate-950 font-sans font-bold text-[20px] px-7 py-4 rounded-sm shadow transition-all flex items-center gap-4 whitespace-nowrap cursor-pointer"
              >
                <span>Ship Now</span>
                <ChevronDown className="w-6 h-6 text-slate-500" />
              </button>

              <AnimatePresence>
                {activeDropdown === "shipNow" && (
                  <motion.div
                    {...dropdownMotion}
                    className="absolute right-0 top-full w-64 bg-white border border-slate-200 rounded-lg shadow-2xl p-3 grid grid-cols-1 gap-1.5 z-50 text-slate-800"
                  >
                    {shipNowMenu.map((item) => (
                      <button
                        key={item.name}
                        onClick={() => handleShipNowClick(item.id, item.tab)}
                        className="flex items-start gap-2.5 p-2 rounded-md text-left hover:bg-slate-100 transition-colors cursor-pointer"
                      >
                        <Ship className="w-4 h-4 text-[#263f98] mt-1 shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-slate-900">
                            {item.name}
                          </p>
                          <p className="text-[10px] text-slate-500 leading-tight">
                            {item.desc}
                          </p>
                        </div>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* MOBILE MENU TOGGLE BUTTON */}
          <div className="lg:hidden flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 hover:bg-zinc-850"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE DROPDOWN DRAWER */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden border-t border-zinc-800 bg-[#16171b] overflow-hidden"
          >
            <div className="px-4 py-4 space-y-2.5 font-sans">
              <p className="text-[10px] uppercase font-bold tracking-wider text-[#263f98] px-3">
                Services
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {services.map((svc) => (
                  <button
                    key={svc.id}
                    onClick={() => handleItemClick(svc.id)}
                    className="text-left text-xs text-slate-200 hover:text-white py-1.5 px-3 rounded hover:bg-zinc-800"
                  >
                    {svc.name}
                  </button>
                ))}
              </div>

              <div className="border-t border-zinc-800/80 my-2" />
              <p className="text-[10px] uppercase font-bold tracking-wider text-[#263f98] px-3">
                Solutions
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {solutions.map((sol) => (
                  <button
                    key={sol.id}
                    onClick={() => handleItemClick(sol.id)}
                    className="text-left text-xs text-slate-200 hover:text-white py-1.5 px-3 rounded hover:bg-zinc-800"
                  >
                    {sol.name}
                  </button>
                ))}
              </div>

              <div className="border-t border-zinc-800/80 my-2" />
              <p className="text-[10px] uppercase font-bold tracking-wider text-[#263f98] px-3">Partner</p><div className="grid grid-cols-2 gap-1.5">{partners.map((item) => <button key={item.id} onClick={() => handleItemClick(item.id)} className="text-left text-xs text-slate-200 py-2 px-3 rounded hover:bg-zinc-800">{item.name}</button>)}</div>
              <p className="text-[10px] uppercase font-bold tracking-wider text-[#263f98] px-3">Company</p><div className="grid grid-cols-2 gap-1.5">{company.map((item) => <button key={item.id} onClick={() => handleItemClick(item.id)} className="text-left text-xs text-slate-200 py-2 px-3 rounded hover:bg-zinc-800">{item.name}</button>)}</div>
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => handleItemClick("tracking")}
                  className="block text-left text-sm text-slate-200 font-bold py-2 px-3 rounded hover:bg-zinc-800"
                >
                  Track Shipment
                </button>
                <button
                  onClick={() => handleItemClick("dashboard")}
                  className="block text-left text-sm text-slate-200 font-bold py-2 px-3 rounded hover:bg-zinc-800"
                >
                  Merchant Portal
                </button>
                <button
                  onClick={() => handleItemClick("admin")}
                  className="block text-left text-sm text-[#93a4ff] font-bold py-2 px-3 rounded hover:bg-zinc-800"
                >
                  🔴 Operations Desk Console
                </button>
              </div>

              <div className="border-t border-zinc-800/80 my-2 pt-2" />
              <button
                onClick={() => handleShipNowClick("dashboard", "booking")}
                className="w-full bg-white hover:bg-slate-50 text-slate-900 font-bold py-3 px-4 rounded text-center text-sm shadow flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Ship className="w-4 h-4 text-[#263f98]" />
                Ship Now (Calculator)
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}



